import React from "react";
import PropTypes from "prop-types";
import { compose, bindActionCreators } from "redux";
import { connect } from "react-redux";
import { withTranslation } from "react-i18next";
import {
  loadWheelSizes,
  loadServiceLinking,
  loadOfferFinancialServices,
  computeOfferFinancialServices,
  saveOfferFinancialServices,
  cancelFinancialServicesChanges,
  changeEditableProperty
} from "../actionCreators";
import { isMandatory, isInRange, validationEngine, foreach, isGreaterThan } from "utils/simple-validations";
//sub-components
import OfferFinancialServicesCard from "./OfferFinancialServicesCard";
// core components
import * as numericLimits from "constants/numericLimits";
import withCustomComponent from "providers/withCustomComponent";
// import memoize from 'lodash.memoize';

export const getValidationRules = () => {
  return {
    header: {
      km: [
        isMandatory(),
        isInRange(
          document =>
            document.header && document.header.productKmMinValue
              ? document.header.productKmMinValue
              : numericLimits.minimumNaturalNumber,
          document =>
            document.header && document.header.productKmMaxValue
              ? document.header.productKmMaxValue
              : numericLimits.maximumOdometer
        )
      ]
    },
    services: foreach(service => ({
      discount: [isInRange(numericLimits.minPercent, numericLimits.maxPercent)],
      period: [isMandatory()],
      fromInstallment: [isMandatory(), isGreaterThan(numericLimits.minimumNaturalNumber)],
      toInstallment: [
        isMandatory(),
        isInRange(service.fromInstallment || numericLimits.minimumNaturalNumber, service.period)
      ],
      periodicity: [isMandatory()]
    })),
    categoryServices: foreach({
      services: foreach(service => ({
        discount: [isInRange(numericLimits.minPercent, numericLimits.maxPercent)],
        period: [isMandatory()],
        fromInstallment: [isMandatory(), isGreaterThan(numericLimits.minimumNaturalNumber)],
        toInstallment: [
          isMandatory(),
          isInRange(service.fromInstallment || numericLimits.minimumNaturalNumber, service.period)
        ],
        periodicity: [isMandatory()]
      }))
    })
  };
};

const isNotEmpty = value => {
  return value !== undefined && value !== null && value !== "";
};

export const mergeServiceUpdates = (document, serviceUpdates) => {
  let categoryServicesNew = document.categoryServices.map(a => {
    return Object.assign({}, a, {
      services: a.services.map(b => {
        return Object.assign({}, b);
      })
    });
  });

  serviceUpdates.forEach(a => {
    let category = categoryServicesNew.find(b => b.serviceCategoryId === a.serviceCategoryId);

    if (category && Object.keys(category).length !== 0) {
      let serviceIndex = category.services.findIndex(x => x.serviceTypeId === a.serviceTypeId);

      category.services = Object.assign([...category.services], {
        [serviceIndex]: {
          ...category.services[serviceIndex],
          serviceMonthlyAmount: a.serviceMonthlyAmount,
          percent: a.percent,
          value: a.value
        }
      });

      if (category.services && category.services.filter(a => a.selected).map(s => s.serviceMonthlyAmount).length > 0)
        category.serviceMonthlyAmount = category.services
          .filter(a => a.selected)
          .map(s => s.serviceMonthlyAmount)
          .reduce((acc, a) => acc + a, 0);
    }
  });

  return {
    ...document,
    services: document.services.map(service => {
      const update = serviceUpdates.find(
        x => x.serviceTypeId === service.serviceTypeId && x.serviceCategoryId === null
      );
      return update ? { ...service, ...update } : service;
    }),
    categoryServices: categoryServicesNew
  };
};

export const serviceSelectionUpdater = (serviceTypeId, serviceCategoryId, selected, propName, value) => prevState => {
  let validationInfo = validationEngine.validate(prevState.document, getValidationRules());
  if (isNotEmpty(serviceCategoryId)) {
    const categoryIndex = prevState.document.categoryServices.findIndex(x => x.serviceCategoryId === serviceCategoryId);

    let serviceIndex = null;

    if (categoryIndex === -1) {
      return prevState;
    }

    if (isNotEmpty(serviceTypeId)) {
      serviceIndex = prevState.document.categoryServices[categoryIndex].services.findIndex(
        x => x.serviceTypeId === serviceTypeId
      );
      if (categoryIndex === -1 || serviceIndex === -1) {
        return prevState;
      }
    }

    let newServices = null;
    if (isNotEmpty(serviceTypeId)) {
      newServices = selected
        ? Object.assign([...prevState.document.categoryServices[categoryIndex].services], {
            [serviceIndex]: {
              ...prevState.document.categoryServices[categoryIndex].services[serviceIndex],
              selected: true,
              [propName]: value
            }
          })
        : Object.assign([...prevState.document.categoryServices[categoryIndex].services], {
            [serviceIndex]: {
              ...prevState.document.categoryServices[categoryIndex].services[serviceIndex],
              selected: false,
              percent: null,
              value: null,
              serviceMonthlyAmount: null,
              discount: null
            }
          });

      let allServicesSelected = newServices.every(a => a.selected);

      let newCategoryServices = Object.assign([...prevState.document.categoryServices], {
        [categoryIndex]: {
          ...prevState.document.categoryServices[categoryIndex],
          services: newServices,
          isSelected: allServicesSelected,
          serviceMonthlyAmount: newServices
            .filter(a => a.selected)
            .map(s => s.serviceMonthlyAmount)
            .reduce((acc, a) => acc + a, 0)
        }
      });

      if (!validationInfo.categoryServices[categoryIndex].services[serviceIndex].isValid) {
        let validationInfoServices = {
          ...validationInfo.validationInfo.categoryServices[categoryIndex].map(s => ({
            ...s,
            discount: "",
            isValid: true
          }))
        };
        let validationInfoCategory = {
          ...validationInfo.categoryServices.map(a => ({ ...a, services: validationInfoServices }))
        };
        validationInfo = Object.assign(validationInfo, validationInfoCategory);
      }

      let newDocument = {
        ...prevState.document,
        categoryServices: newCategoryServices
      };

      return {
        document: newDocument,
        validationInfo,
        isDirty: true
      };
    } else {
      let selectedServices = null;
      if (selected)
        selectedServices = prevState.document.categoryServices[categoryIndex].services.map(s => ({
          ...s,
          selected: true
        }));
      else
        selectedServices = prevState.document.categoryServices[categoryIndex].services.map(s => ({
          ...s,
          selected: false,
          value: null,
          serviceMonthlyAmount: null,
          discount: null
        }));

      let newCategoryServices = selected
        ? Object.assign([...prevState.document.categoryServices], {
            [categoryIndex]: {
              ...prevState.document.categoryServices[categoryIndex],
              services: selectedServices,
              isSelected: true
            }
          })
        : Object.assign([...prevState.document.categoryServices], {
            [categoryIndex]: {
              ...prevState.document.categoryServices[categoryIndex],
              services: selectedServices,
              isSelected: false,
              serviceMonthlyAmount: null
            }
          });

      let newDocument = {
        ...prevState.document,
        categoryServices: newCategoryServices
      };

      return {
        document: newDocument,
        validationInfo,
        isDirty: true
      };
    }
  } else {
    const serviceIndex = prevState.document.services.findIndex(x => x.serviceTypeId === serviceTypeId);
    if (serviceIndex === -1) {
      return prevState;
    }

    if (!validationInfo.services[serviceIndex].isValid) {
      validationInfo = {
        ...validationInfo,
        services: Object.assign(
          { ...validationInfo.services },
          { [serviceIndex]: { ...validationInfo.services[serviceIndex], discount: "", isValid: true } }
        )
      };
    }

    return {
      document: {
        ...prevState.document,
        services: selected
          ? Object.assign([...prevState.document.services], {
              [serviceIndex]: { ...prevState.document.services[serviceIndex], selected: true, [propName]: value }
            })
          : Object.assign([...prevState.document.services], {
              [serviceIndex]: {
                ...prevState.document.services[serviceIndex],
                selected: false,
                percent: null,
                value: null,
                serviceMonthlyAmount: null,
                discount: null
              }
            })
      },
      validationInfo,
      isDirty: true
    };
  }
};

export const itemUpdater = (serviceTypeId, serviceCategoryId, propName, value) => prevState => {
  if (isNotEmpty(serviceCategoryId)) {
    const categoryIndex = prevState.document.categoryServices.findIndex(x => x.serviceCategoryId === serviceCategoryId);
    const serviceIndex = prevState.document.categoryServices[categoryIndex].services.findIndex(
      x => x.serviceTypeId === serviceTypeId
    );

    if (categoryIndex === -1 || serviceIndex === -1) {
      return prevState;
    }

    let newServices = Object.assign([...prevState.document.categoryServices[categoryIndex].services], {
      [serviceIndex]: {
        ...prevState.document.categoryServices[categoryIndex].services[serviceIndex],
        [propName]: value
      }
    });

    let newCategoryServices = Object.assign([...prevState.document.categoryServices], {
      [categoryIndex]: { ...prevState.document.categoryServices[categoryIndex], services: newServices }
    });

    let newDocument = {
      ...prevState.document,
      categoryServices: newCategoryServices
    };

    const validationInfo = validationEngine.validate(newDocument, getValidationRules());

    return {
      document: newDocument,
      validationInfo,
      isDirty: true
    };
  } else {
    const serviceIndex = prevState.document.services.findIndex(x => x.serviceTypeId === serviceTypeId);
    if (serviceIndex === -1) {
      return prevState;
    }

    const document = {
      ...prevState.document,
      services: Object.assign([...prevState.document.services], {
        [serviceIndex]: { ...prevState.document.services[serviceIndex], [propName]: value }
      })
    };
    const validationInfo = validationEngine.validate(document, getValidationRules());

    return {
      document,
      validationInfo,
      isDirty: true
    };
  }
};

export const headerUpdater = (prop, value) => prevState => {
  const document = { ...prevState.document, header: { ...prevState.document.header, [prop]: value } };
  const validationInfo = validationEngine.validate(document, getValidationRules());
  return {
    document,
    validationInfo,
    isDirty: true
  };
};

export const dispatchHeaderPropertyChangeEffects = (props, state) => {
  let servicesToCompute = state.document.services
    .filter((_, index) => state.validationInfo.services[index].isValid)
    .filter(x => x.selected)
    .map(y => ({ serviceTypeId: y.serviceTypeId, discount: y.discount, serviceCategoryId: y.serviceCategoryId }));

  let servicesCategoriesToCompute = state.document.categoryServices.map((category, categoryIndex) => {
    const a = category.services
      .filter((__, serviceIndex) => state.validationInfo.categoryServices[categoryIndex].services[serviceIndex].isValid)
      .filter(a => a.selected)
      .map(a => ({ serviceTypeId: a.serviceTypeId, discount: a.discount, serviceCategoryId: a.serviceCategoryId }));
    return a;
  });

  if (servicesCategoriesToCompute.length > 0) {
    let servicesFromCategoriesToCompute = servicesCategoriesToCompute.reduce(selectMany(a => a, []));
    let allServicesToCompute = servicesToCompute.concat(servicesFromCategoriesToCompute);
    if (state.validationInfo.header.isValid) {
      dispatchCompute(props, state, allServicesToCompute);
    }
  }
};

export function selectMany(f) {
  return function (acc, b) {
    return acc.concat(f(b));
  };
}

export function getServicesFormCategories(document) {
  let servicesFromCategories = document.categoryServices.reduce(
    selectMany(a => a.services),
    []
  );
  let allServices = document.services.concat(servicesFromCategories);
  return allServices;
}

export const dispatchCompute = (props, state, serviceDetails) => {
  props.actions.computeOfferFinancialServices(props.documentId, props.siteId, state.document.header, serviceDetails);
};

export const dispatchSelectionChangeEffects = (serviceTypeId, serviceCategoryId, props, state) => {
  let validationInfo = validationEngine.validate(state.document, getValidationRules());

  if (isNotEmpty(serviceCategoryId)) {
    const categoryIndex = state.document.categoryServices.findIndex(x => x.serviceCategoryId === serviceCategoryId);
    if (isNotEmpty(serviceTypeId)) {
      const selected = state.document.categoryServices
        .find(x => x.serviceCategoryId === serviceCategoryId)
        .services.find(x => x.serviceTypeId === serviceTypeId).selected;
      const serviceIndex = state.document.categoryServices[categoryIndex].services.findIndex(
        x => x.serviceTypeId === serviceTypeId
      );
      if (
        categoryIndex !== -1 &&
        serviceIndex !== -1 &&
        validationInfo.categoryServices[categoryIndex].services[serviceIndex].isValid &&
        validationInfo.header.isValid &&
        selected
      ) {
        const serviceDetail = {
          serviceTypeId: serviceTypeId,
          discount: state.document.categoryServices[categoryIndex].services.find(x => x.serviceTypeId === serviceTypeId)
            .discount,
          serviceCategoryId: state.document.categoryServices[categoryIndex].services.find(
            x => x.serviceTypeId === serviceTypeId
          ).serviceCategoryId
        };
        dispatchCompute(props, state, [serviceDetail]);
      }
    } else {
      const selected = state.document.categoryServices.find(x => x.serviceCategoryId === serviceCategoryId).isSelected;
      if (
        categoryIndex !== -1 &&
        validationInfo.categoryServices[categoryIndex].isValid &&
        validationInfo.header.isValid &&
        selected
      ) {
        const serviceDetails = state.document.categoryServices[categoryIndex].services.map(a => ({
          serviceTypeId: a.serviceTypeId,
          discount: a.discount,
          serviceCategoryId: a.serviceCategoryId
        }));
        dispatchCompute(props, state, serviceDetails);
      }
    }
  } else {
    const selected = state.document.services.find(x => x.serviceTypeId === serviceTypeId).selected;
    const serviceIndex = state.document.services.findIndex(x => x.serviceTypeId === serviceTypeId);

    if (
      serviceIndex !== -1 &&
      validationInfo.services[serviceIndex].isValid &&
      validationInfo.header.isValid &&
      selected
    ) {
      const serviceDetail = {
        serviceTypeId: serviceTypeId,
        discount: state.document.services.find(x => x.serviceTypeId === serviceTypeId).discount,
        serviceCategoryId: state.document.services.find(x => x.serviceTypeId === serviceTypeId).serviceCategoryId
      };
      dispatchCompute(props, state, [serviceDetail]);
    }
  }
};

const initialValidationInfo = {
  isValid: true,
  services: {},
  header: {},
  categoryServices: {
    services: {}
  }
};

export class OfferFinancialServicesContainer extends React.PureComponent {
  state = {
    document: null,
    validationInfo: initialValidationInfo,
    isDirty: false,
    prevProps: {
      document: null,
      serviceUpdates: []
    }
  };
  passedServiceTypeList = [];
  selectionConfirmed = false;
  cancelSelection = false;

  static getDerivedStateFromProps(props, state) {
    const prevProps = state.prevProps;
    //react to document prop change
    if (props.document !== prevProps.document) {
      return {
        prevProps: { document: props.document, serviceUpdates: props.serviceUpdates },
        document: props.document,
        isValid: true,
        validationInfo: initialValidationInfo,
        isDirty: false,
        editable: false
      };
    }
    //react to serviceUpdates prop change
    if (props.serviceUpdates !== prevProps.serviceUpdates) {
      return {
        prevProps: { ...prevProps, serviceUpdates: props.serviceUpdates },
        document: mergeServiceUpdates(state.document, props.serviceUpdates),
        isDirty: true
      };
    }

    return null;
  }

  componentDidMount() {
    const { actions, documentId, siteId } = this.props;
    actions.loadOfferFinancialServices(documentId, siteId);
    actions.loadWheelSizes(documentId, siteId);
    actions.loadServiceLinking(documentId, siteId);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.documentLoaded !== this.props.documentLoaded && !this.props.documentLoaded) {
      this.props.actions.loadOfferFinancialServices(this.props.documentId, this.props.siteId);
      this.props.actions.loadWheelSizes(this.props.documentId, this.props.siteId);
    }
  }

  componentWillUnmount() {
    this.props.actions.cancelFinancialServicesChanges(this.props.documentId, this.props.siteId);
  }

  handleServiceSelectionChange = (serviceTypeId, serviceCategoryId, selected) => {
    this.passedServiceTypeList = [];
    this.selectionConfirmed = false;
    this.cancelSelection = false;
    const categoryIndex = this.props.document.categoryServices.findIndex(
      x => x.serviceCategoryId === serviceCategoryId
    );

    let serviceIndex = null;

    if (isNotEmpty(serviceTypeId) && categoryIndex > -1) {
      serviceIndex = this.props.document.categoryServices[categoryIndex].services.findIndex(
        x => x.serviceTypeId === serviceTypeId
      );
    }

    if (serviceIndex !== null) {
      const serviceTypeId = this.props.document.categoryServices[categoryIndex].services[serviceIndex].serviceTypeId;
      this.processLinkedServices(serviceTypeId, selected);
    }

    if (categoryIndex > -1 && serviceIndex === null) {
      this.props.document.categoryServices[categoryIndex].services.forEach(service => {
        this.processLinkedServices(service.serviceTypeId, selected);
      });
    }

    if (isNotEmpty(serviceTypeId) && categoryIndex === -1) {
      this.processLinkedServices(serviceTypeId, selected);
    }
  };

  processLinkedServices = (serviceTypeId, selected) => {
    const linkedServices = this.props.serviceLinkingList;
    const service = [];
    this.props.document.categoryServices.forEach(categService => {
      const sourceCategService = categService.services.filter(service => service.serviceTypeId === serviceTypeId);
      if (sourceCategService.length > 0) service.push(...sourceCategService);
    });

    const sourceService = this.props.document.services.filter(service => service.serviceTypeId === serviceTypeId);
    if (sourceService.length > 0) service.push(...sourceService);

    this.passedServiceTypeList.push(service[0].serviceTypeId);
    const listServiceLinked = linkedServices.filter(
      x => x.destServiceTypeId === serviceTypeId || x.sourceServiceTypeId === serviceTypeId
    );
    if (listServiceLinked.length === 0) {
      this.setState(serviceSelectionUpdater(service[0].serviceTypeId, service[0].serviceCategoryId, selected), () =>
        dispatchSelectionChangeEffects(service[0].serviceTypeId, service[0].serviceCategoryId, this.props, this.state)
      );
      return;
    }

    this.confirmSelection(listServiceLinked, service, selected);
    if (!this.cancelSelection)
      this.setState(serviceSelectionUpdater(service[0].serviceTypeId, service[0].serviceCategoryId, selected), () =>
        dispatchSelectionChangeEffects(service[0].serviceTypeId, service[0].serviceCategoryId, this.props, this.state)
      );

    listServiceLinked.forEach(x => {
      let servicesToLink = [];
      this.props.document.categoryServices.forEach(categService => {
        const sourceCategService = categService.services.filter(
          service => service.serviceTypeId === x.sourceServiceTypeId || service.serviceTypeId === x.destServiceTypeId
        );
        if (sourceCategService.length > 0) {
          servicesToLink.push(...sourceCategService);
        }
      });

      servicesToLink.forEach(serviceToLink => {
        if (this.passedServiceTypeList.filter(x => x === serviceToLink.serviceTypeId).length === 0)
          if (x.linkingTypeCode === "EXCLUDE") {
            if (selected === true)
              this.processLinkedServices(serviceToLink.serviceTypeId, serviceToLink.serviceCategoryId, !selected);
          } else this.processLinkedServices(serviceToLink.serviceTypeId, serviceToLink.serviceCategoryId, selected);
      });
    });
  };

  confirmSelection = (listServiceLinked, service, selected) => {
    if (!this.selectionConfirmed) {
      let message = "";
      listServiceLinked.forEach(serv => {
        if (serv.linkingTypeCode === "LINK") {
          message =
            message +
            this.props.t("OfferEdit.FinancialServices.LinkService", {
              serv: {
                sourceServiceName: serv.sourceServiceName || "",
                destServiceName: serv.destServiceName || ""
              }
            }) +
            "\n";
        } else
          message =
            message +
            this.props.t("OfferEdit.FinancialServices.ExcludeService", {
              serv: {
                sourceServiceName: serv.sourceServiceName || "",
                destServiceName: serv.destServiceName || ""
              }
            }) +
            "\n";
      });

      if (listServiceLinked.length > 0) {
        this.selectionConfirmed = true;
        if (window.confirm(message + this.props.t("OfferEdit.FinancialServices.ConfirmationMessage"))) {
          this.setState(serviceSelectionUpdater(service[0].serviceTypeId, service[0].serviceCategoryId, selected), () =>
            dispatchSelectionChangeEffects(
              service[0].serviceTypeId,
              service[0].serviceCategoryId,
              this.props,
              this.state
            )
          );
        } else {
          this.cancelSelection = true;
          return;
        }
      } else
        this.setState(serviceSelectionUpdater(service[0].serviceTypeId, service[0].serviceCategoryId, selected), () =>
          dispatchSelectionChangeEffects(service[0].serviceTypeId, service[0].serviceCategoryId, this.props, this.state)
        );
    }
  };

  handleServiceItemPropertyChange = (serviceTypeId, serviceCategoryId, propName, value) => {
    if (propName !== undefined) {
      this.setState(itemUpdater(serviceTypeId, serviceCategoryId, propName, value));
    }
  };

  handleHeaderPropertyChange = prop => value => {
    this.setState(headerUpdater(prop, value), () => dispatchHeaderPropertyChangeEffects(this.props, this.state));
  };

  handleSave = () => {
    const validationInfo = validationEngine.validate(this.state.document, getValidationRules());
    if (!validationInfo.isValid) {
      this.setState({
        validationInfo
      });
      return;
    }

    this.props.actions.saveOfferFinancialServices(
      this.props.documentId,
      this.props.siteId,
      this.state.document,
      getServicesFormCategories(this.state.document)
    );
  };

  handleServiceItemComputeValue = (serviceTypeId, serviceCategoryId) => {
    dispatchSelectionChangeEffects(serviceTypeId, serviceCategoryId, this.props, this.state);
  };

  handleModifyData = () => {
    this.props.actions.changeEditableProperty(true);
  };

  render() {
    const { wheelSizes, saving, servicesLoading, editable } = this.props;
    const { document, isDirty, validationInfo } = this.state;
    return (
      <OfferFinancialServicesCard
        document={document}
        servicesLoading={servicesLoading}
        wheelSizes={wheelSizes}
        validationInfo={validationInfo}
        onServiceSelectionChange={this.handleServiceSelectionChange}
        onHeaderPropertyChange={this.handleHeaderPropertyChange}
        onSave={this.handleSave}
        isDirty={isDirty}
        saving={saving}
        onServiceItemPropertyChange={this.handleServiceItemPropertyChange}
        onServiceItemComputeValue={this.handleServiceItemComputeValue}
        editable={editable}
        handleModifyData={this.handleModifyData}
      />
    );
  }
}

OfferFinancialServicesContainer.propTypes = {
  documentId: PropTypes.number.isRequired,
  siteId: PropTypes.number.isRequired,
  document: PropTypes.object.isRequired,
  serviceUpdates: PropTypes.array.isRequired,
  wheelSizes: PropTypes.array.isRequired,
  serviceLinkingList: PropTypes.array.isRequired,
  actions: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
  saving: PropTypes.bool.isRequired,
  servicesLoading: PropTypes.array.isRequired,
  documentLoaded: PropTypes.bool.isRequired,
  editable: PropTypes.bool.isRequired,
  dispatch: PropTypes.func.isRequired
};

export function mapStateToProps(state) {
  const {
    document,
    serviceUpdates,
    wheelSizes,
    serviceLinkingList,
    saving,
    servicesLoading,
    documentLoaded,
    editable
  } = state.currentOffer.financialServicesData;
  return {
    document,
    serviceUpdates,
    wheelSizes,
    serviceLinkingList,
    saving,
    servicesLoading,
    documentLoaded,
    editable
  };
}

export function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        loadOfferFinancialServices,
        computeOfferFinancialServices,
        saveOfferFinancialServices,
        cancelFinancialServicesChanges,
        loadWheelSizes,
        loadServiceLinking,
        changeEditableProperty
      },
      dispatch
    ),
    dispatch
  };
}

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withTranslation()
)(withCustomComponent("OfferFinancialServicesContainer")(OfferFinancialServicesContainer));
